import React, { useState } from "react";
import { SketchPicker } from "react-color";
import "./Chart.css"; // Ensure you have a corresponding CSS file for styling

const CHart = ({ setChartConfig, chartConfig }) => {
  const [showLineColorPicker, setShowLineColorPicker] = useState(false);
  const [showGradientToColorPicker, setShowGradientToColorPicker] = useState(false);

  const handleStroke = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      stroke: { ...prevConfig.stroke, [key]: value }
    }));
  };

  const handleFillAndStrokeChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      fill: { ...prevConfig.fill, [key]: value }
    }));
  };

  const handleGradientChange = (key, value) => {
    setChartConfig((prevConfig) => ({
      ...prevConfig,
      fill: {
        ...prevConfig.fill,
        gradient: {
          ...prevConfig.fill.gradient,
          [key]: value
        }
      }
    }));
  };

  return (
    <div className="fill-and-stroke-settings-container">
      {/* Title with Dropdown Icon */}
      <div className="fill-and-stroke-settings-header">
        <h3>Fill & Stroke Settings</h3>
      </div>

      {/* Fill & Stroke Settings */}
      <div className="fill-and-stroke-settings">
        {/* Fill Opacity */}
        <div className="fill-and-stroke-settings-section">
          <label className="fill-and-stroke-settings-label">Fill Opacity</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={chartConfig?.fill?.opacity}
            onChange={(e) => handleFillAndStrokeChange("opacity", Number(e.target.value))}
          />
        </div>

        {/* Fill Type */}
        <div className="fill-and-stroke-settings-section">
          <label className="fill-and-stroke-settings-label">Fill Type</label>
          <select
            value={chartConfig?.fill?.type}
            onChange={(e) => handleFillAndStrokeChange("type", e.target.value)}
            className="fill-and-stroke-settings-dropdown"
          >
            <option value="solid">Solid</option>
            <option value="gradient">Gradient</option>
            <option value="pattern">Pattern</option>
          </select>
        </div>

        {/* Gradient Options (Conditional Rendering) */}
        {chartConfig?.fill?.type === "gradient" && (
          <div className="gradient-options">
            {/* Shade */}
            <div className="fill-and-stroke-settings-section">
              <label className="fill-and-stroke-settings-label">Shade</label>
              <select
                value={chartConfig?.fill?.gradient?.shade || "dark"}
                onChange={(e) => handleGradientChange("shade", e.target.value)}
                className="fill-and-stroke-settings-dropdown"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>

            {/* Direction */}
            <div className="fill-and-stroke-settings-section">
              <label className="fill-and-stroke-settings-label">Direction</label>
              <select
                value={chartConfig?.fill?.gradient?.type || "horizontal"}
                onChange={(e) => handleGradientChange("type", e.target.value)}
                className="fill-and-stroke-settings-dropdown"
              >
                <option value="horizontal">Horizontal</option>
                <option value="vertical">Vertical</option>
                <option value="diagonal">Diagonal</option>
                <option value="diagonal2">Diagonal 2</option>
              </select>
            </div>

            {/* Inverse Direction */}
            <div className="fill-and-stroke-settings-section">
              <label className="fill-and-stroke-settings-label">Inverse Direction</label>
              <input
                type="checkbox"
                style={{ width: "fit-content" }}
                checked={chartConfig?.fill?.gradient?.inverseColors}
                onChange={(e) => handleGradientChange("inverseColors", e.target.checked)}
              />
            </div>

            {/* Shade Intensity */}
            <div className="fill-and-stroke-settings-section">
              <label className="fill-and-stroke-settings-label">Shade Intensity</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={chartConfig?.fill?.gradient?.shadeIntensity || 0.5}
                onChange={(e) => handleGradientChange("shadeIntensity", Number(e.target.value))}
              />
            </div>

            {/* Gradient To Colors */}
            <div className="color-picker-wrapper">
              <label className="fill-and-stroke-settings-label">Gradient To Colors</label>
              <button
                className="color-picker-button"
                onClick={() => setShowGradientToColorPicker(!showGradientToColorPicker)}
              >
                <div
                  className="color-swatch"
                  style={{
                    backgroundColor: chartConfig?.fill?.gradient?.gradientToColors?.[0] || "#000000"
                  }}
                />
              </button>
              {showGradientToColorPicker && (
                <div className="picker-popover">
                  <div className="picker-cover" onClick={() => setShowGradientToColorPicker(false)} />
                  <SketchPicker
                    color={chartConfig?.fill?.gradient?.gradientToColors?.[0] || "#000000"}
                    onChangeComplete={(color) => handleGradientChange("gradientToColors", [color.hex])}
                  />
                </div>
              )}
            </div>

            {/* Gradient From Opacity */}
            <div className="fill-and-stroke-settings-section">
              <label className="fill-and-stroke-settings-label">Gradient From Opacity</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={chartConfig?.fill?.gradient?.opacityFrom || 1}
                onChange={(e) => handleGradientChange("opacityFrom", Number(e.target.value))}
              />
            </div>

            {/* Gradient To Opacity */}
            <div className="fill-and-stroke-settings-section">
              <label className="fill-and-stroke-settings-label">Gradient To Opacity</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={chartConfig?.fill?.gradient?.opacityTo || 1}
                onChange={(e) => handleGradientChange("opacityTo", Number(e.target.value))}
              />
            </div>
          </div>
        )}

        {/* Show Stroke */}
        <div className="fill-and-stroke-settings-section">
          <label className="fill-and-stroke-settings-label">Show Stroke / Outline</label>
          <input
            type="checkbox"
            style={{ width: "fit-content" }}
            checked={chartConfig?.stroke?.show || false}
            onChange={(e) => handleStroke("show", e.target.checked)}
          />
        </div>

        {/* Line Colors */}
        <div className="color-picker-wrapper">
          <label className="fill-and-stroke-settings-label">Line Colors</label>
          <button className="color-picker-button" onClick={() => setShowLineColorPicker(!showLineColorPicker)}>
            <div
              className="color-swatch"
              style={{
                backgroundColor: chartConfig?.stroke?.colors?.[0] || "#000000"
              }}
            />
          </button>
          {showLineColorPicker && (
            <div className="picker-popover">
              <div className="picker-cover" onClick={() => setShowLineColorPicker(false)} />
              <SketchPicker
                color={chartConfig?.stroke?.colors?.[0] || "#000000"}
                onChangeComplete={(color) => handleStroke("colors", [color.hex])}
              />
            </div>
          )}
        </div>

        {/* Line Width */}
        <div className="fill-and-stroke-settings-section">
          <label className="fill-and-stroke-settings-label">Line Width</label>
          <input
            type="number"
            value={chartConfig?.stroke?.width}
            onChange={(e) => handleStroke("width", Number(e.target.value))}
          />
        </div>

        {/* Line Cap */}
        <div className="fill-and-stroke-settings-section">
          <label className="fill-and-stroke-settings-label">Line Cap</label>
          <select
            value={chartConfig?.stroke?.lineCap || "round"}
            onChange={(e) => handleStroke("lineCap", e.target.value)}
            className="fill-and-stroke-settings-dropdown"
          >
            <option value="round">Round</option>
            <option value="butt">Butt</option>
            <option value="square">Square</option>
          </select>
        </div>

        {/* Line Dash Array */}
        <div className="fill-and-stroke-settings-section">
          <label className="fill-and-stroke-settings-label">Line Dash Array</label>
          <input
            type="number"
            value={chartConfig?.stroke?.dashArray || "0"}
            onChange={(e) => handleStroke("dashArray", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default CHart;
